"use client"

import { useState } from "react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { oneLight, oneDark } from "react-syntax-highlighter/dist/esm/styles/prism"
import { Copy, Check, CircleAlert } from "lucide-react"
import { useIsDark } from "@/components/theme/theme"
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table"

// Close an unterminated code fence so a streaming half-message still renders as
// code instead of leaking raw backticks into the prose.
function completePartialMarkdown(text: string): string {
  const fenceCount = (text.match(/^```/gm) || []).length
  if (fenceCount % 2 !== 0) return text + "\n```"
  return text
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      onClick={handleCopy}
      className="p-1 text-muted-foreground transition-colors hover:text-foreground"
      aria-label="Copiar código"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  )
}

// react-markdown v10 + React 19 has overly strict component types for
// element-specific refs. Record<string, ...> avoids the mismatch on pre/code/img.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildComponents(isDark: boolean): Record<string, any> {
  return {
    code({ className, children }: { className?: string; children?: React.ReactNode }) {
      const match = /language-(\w+)/.exec(className || "")
      const codeString = String(children).replace(/\n$/, "")
      if (match) {
        return (
          <div className="my-2 overflow-hidden rounded-md border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border bg-muted px-3 py-1">
              <span className="text-xs text-muted-foreground">{match[1]}</span>
              <CopyButton text={codeString} />
            </div>
            <SyntaxHighlighter
              style={isDark ? oneDark : oneLight}
              language={match[1]}
              PreTag="div"
              customStyle={{
                margin: 0,
                padding: "0.75rem",
                fontSize: "0.8rem",
                background: "transparent",
              }}
            >
              {codeString}
            </SyntaxHighlighter>
          </div>
        )
      }
      return (
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{children}</code>
      )
    },
    pre({ children }: { children?: React.ReactNode }) {
      return <>{children}</>
    },
    // Constrain markdown-embedded images (![alt](url)) so they don't overflow
    // the message column. No HTML is enabled — rehype-raw is intentionally absent.
    img({ src, alt }: { src?: string; alt?: string }) {
      return (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="my-2 max-w-full rounded border border-border"
        />
      )
    },
    table({ children }: { children?: React.ReactNode }) {
      // Figma table: an outer rounded card with generous white padding; the
      // table inside has square corners, a darker header, zebra body rows, and
      // NO row borders — the zebra alone separates rows. Zebra is scoped to
      // tbody rows so the header isn't tinted.
      return (
        <div className="my-2 rounded-2xl border border-table-card-border bg-table-card-bg p-4 pt-8 text-sm">
          <div className="overflow-x-auto [&_tbody_tr:nth-child(odd)]:bg-table-zebra [&_tbody_tr:nth-child(even)]:bg-table-zebra-alt [&_tbody_tr]:border-b [&_tbody_tr]:border-table-row-border [&_tr]:hover:bg-transparent">
            <Table>{children}</Table>
          </div>
        </div>
      )
    },
    thead({ children }: { children?: React.ReactNode }) {
      return <TableHeader className="bg-table-header [&_tr]:border-b [&_tr]:border-table-header-border">{children}</TableHeader>
    },
    tbody({ children }: { children?: React.ReactNode }) {
      return <TableBody>{children}</TableBody>
    },
    tr({ children }: { children?: React.ReactNode }) {
      return <TableRow>{children}</TableRow>
    },
    th({ children }: { children?: React.ReactNode }) {
      return <TableHead className="px-3 py-2.5 font-semibold text-foreground">{children}</TableHead>
    },
    td({ children }: { children?: React.ReactNode }) {
      return <TableCell className="px-3 py-2.5">{children}</TableCell>
    },
    blockquote({ children }: { children?: React.ReactNode }) {
      // Figma callout: the agent highlights notes/alerts with a markdown
      // blockquote (`>`), sometimes with an emoji. We render every blockquote as
      // an amber callout with a leading icon. Bold text (`**...**`) reads as the
      // title (weight 600); the rest is the body paragraph (weight 400). Colors
      // come from tokens so light/dark follow the theme.
      return (
        <div className="my-2 flex items-start gap-2.5 rounded-lg border border-callout-border bg-callout-bg px-3.5 py-3 [&_p]:my-0 [&_strong]:font-semibold [&_strong]:text-callout-title [&_p]:font-body [&_p]:text-sm [&_p]:font-normal [&_p]:text-callout-text">
          <CircleAlert className="mt-0.5 size-4 shrink-0 text-callout-icon" aria-hidden="true" />
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      )
    },
  }
}

export function MarkdownRenderer({ content }: { content: string }) {
  const isDark = useIsDark()
  if (!content) return null
  return (
        <div className="markdown-body leading-relaxed [&_p]:my-1.5 [&_ul]:my-1.5 [&_ul]:pl-5 [&_ul]:list-disc [&_ol]:my-1.5 [&_ol]:pl-5 [&_ol]:list-decimal [&_li]:my-0.5 [&_h1]:text-lg [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1.5 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-2.5 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={buildComponents(isDark)}>
        {completePartialMarkdown(content)}
      </ReactMarkdown>
    </div>
  )
}
